import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import FormDialog from "helpers/FormDialog";
import DatatableComponent from "helpers/DatatableComponent";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteDialog from "helpers/DeleteDialog";
import { successToast, errorToast } from "helpers/Notification";
import { mapStateToProps } from "store/helpers/mapState";
import { mapDispatchToProps } from "store/helpers/mapDispatch";
import { connect } from "react-redux";

const ApprovalProcessComponent = ({
  FetchDetails,
  positions: initialPositions,
}) => {
  const [positions, setPositions] = useState(initialPositions || []);
  const [lineManagers, setLineManagers] = useState([]);
  const [open, setOpen] = useState(0);
  const [value, setValue] = useState({
    positionId: "",
    steps: [{ approverPositionId: "" }],
  });
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [maxSteps, setMaxSteps] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [hasLineManager, setHasLineManager] = useState(false);

  useEffect(() => {
    if (initialPositions?.length > 0) {
      setPositions(initialPositions);
    } else {
      const fetchPositions = async () => {
        try {
          const response = await FetchDetails("/api/staff-position/viewAll");
          if (response?.status) {
            setPositions(response?.data?.list || []);
          }
        } catch (error) {
          setError("Failed to load positions");
          console.error("Error fetching positions:", error);
        }
      };
      fetchPositions();
    }
  }, [initialPositions]);

  useEffect(() => {
    if (!open || !value.positionId) {
      return;
    }

    let isSubscribed = true;

    const validatePosition = async () => {
      try {
        const response = await axios.get(
          `/api/approval-process/validate-position/${value.positionId}`
        );

        if (!isSubscribed) return;

        if (response.data.valid) {
          const lineManagerOption = [
            {
              label: response.data.lineManager.name,
              value: response.data.lineManager.id,
            },
          ];

          setHasLineManager(true);
          setLineManagers(lineManagerOption);

          // Don't automatically add line manager to steps
          if (open === 1 && !selectedId) {
            setValue((prev) => ({
              ...prev,
              steps: [{ approverPositionId: "" }],
            }));
          }
        } else {
          setHasLineManager(false);
          setLineManagers([]);
        }
      } catch (error) {
        if (!isSubscribed) return;

        setHasLineManager(false);
        setLineManagers([]);
        errorToast(
          error.response?.data?.message || "Failed to validate position"
        );
      }
    };

    const timeoutId = setTimeout(validatePosition, 300);

    return () => {
      isSubscribed = false;
      clearTimeout(timeoutId);
    };
  }, [value.positionId, open, selectedId]);

  const resetForm = useCallback(() => {
    setValue({
      positionId: "",
      steps: [{ approverPositionId: "" }],
    });
    setSelectedId(null);
    setHasLineManager(false);
  }, []);

  const onSuccess = useCallback(() => {
    setCount((v) => v + 1);
    resetForm();
  }, [resetForm]);

  const preparePayload = (form) => ({
    ...form,
    steps: (form.steps || []).map((step, idx) => ({
      ...step,
      order: idx + 1,
    })),
  });

  const handleEdit = (row) => {
    if (!row) return;

    const sortedSteps = [...(row.steps || [])]
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        approverPositionId: s.approverPositionId,
      }));

    setValue({
      positionId: row.positionId,
      steps: sortedSteps,
    });
    setSelectedId(row.id);
    setOpen(2);
  };

  const handleDelete = (id) => {
    if (!id) return;
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const beforeSubmit = (form) => {
    if (!hasLineManager) {
      errorToast("The position needs to have a line manager.");
      return false;
    }

    if (open === 1) {
      const exists = tableData.some(
        (row) => row.positionId === form.positionId
      );
      if (exists) {
        errorToast("This position already has an approval process.");
        return false;
      }
    }

    // Ensure line manager is included in steps
    const lineManagerId = lineManagers[0]?.value;
    const hasLineManagerStep = form.steps.some(
      (step) => step.approverPositionId === lineManagerId
    );

    if (!hasLineManagerStep) {
      errorToast("The line manager must be included in the approval steps.");
      return false;
    }

    const approverIds = (form.steps || []).map((s) => s.approverPositionId);
    const uniqueApprovers = new Set(approverIds);
    if (uniqueApprovers.size !== approverIds.length) {
      errorToast("Duplicate approver positions are not allowed.");
      return false;
    }

    return preparePayload(form);
  };

  const getOrdinal = (n) => {
    if (n === 1) return "1st";
    if (n === 2) return "2nd";
    if (n === 3) return "3rd";
    return `${n}th`;
  };

  const processData = (data) => {
    const arr = data.data || [];
    setTableData(arr);

    const max = arr.reduce(
      (acc, curr) => Math.max(acc, curr.steps?.length || 0),
      0
    );
    setMaxSteps(max);
    return { data: arr };
  };

  const lineManagerLabel = (text) => (
    <span>
      {text}
      <span
        style={{
          backgroundColor: "#e3f2fd",
          color: "#1976d2",
          padding: "2px 6px",
          borderRadius: "4px",
          marginLeft: "4px",
          fontSize: "0.85em",
          fontWeight: 500,
        }}
      >
        Line Manager
      </span>
    </span>
  );

  const stepColumns = Array.from({ length: maxSteps }, (_, idx) => ({
    name: `step_${idx}`,
    label: `${getOrdinal(idx + 1)} Approver`,
    options: {
      customBodyRender: (_, tableMeta) => {
        const row = tableData[tableMeta.rowIndex] || {};
        const step = row.steps?.[idx];
        const position = positions.find(
          (p) => p.id === step?.approverPositionId
        );
        return step ? position?.name || "Unknown Position" : "-";
      },
    },
  }));

  const getApproverOptions = (stepIndex = 0) => {
    if (!value.steps || !Array.isArray(value.steps)) {
      return [];
    }

    const usedApproverIds = value.steps
      .filter((_, idx) => idx !== stepIndex)
      .map((step) => step.approverPositionId)
      .filter(Boolean);

    return positions
      .filter((pos) => {
        if (open === 2) {
          const currentStepApprover =
            value.steps[stepIndex]?.approverPositionId;
          return (
            pos.id === currentStepApprover || !usedApproverIds.includes(pos.id)
          );
        }
        return !usedApproverIds.includes(pos.id);
      })
      .map((pos) => ({
        value: pos.id,
        label:
          pos.id === lineManagers[0]?.value
            ? lineManagerLabel(pos.name)
            : pos.name,
      }));
  };

  const handlePositionChange = useCallback(
    (val) => {
      setValue((prev) => ({
        ...prev,
        positionId: val,
        steps: open === 1 ? [{ approverPositionId: "" }] : prev.steps,
      }));
    },
    [open]
  );

  const approvalProcessFields = [
    {
      name: "positionId",
      label: "Position",
      type: "select",
      value: value.positionId,
      required: true,
      setValue: handlePositionChange,
      values: positions.map((pos) => ({ value: pos.id, label: pos.name })),
      // values: positions,
      helperText: hasLineManager
        ? "✓ Position has line manager"
        : value.positionId
        ? "⚠ Position needs a line manager"
        : "",
      error: value.positionId && !hasLineManager,
    },
    {
      name: "steps",
      label: "Approval Steps",
      type: "array",
      value: value.steps || [],
      required: true,
      setValue: (steps) => setValue((v) => ({ ...v, steps: steps || [] })),
      itemLabel: (idx) => `${getOrdinal(idx + 1)} Approval`,
      fields: [
        {
          name: "approverPositionId",
          label: "Approver Position",
          type: "select",
          required: true,
          values: (stepIndex) => getApproverOptions(stepIndex) || [],
          disabled: (stepIndex) => stepIndex === 0 && !hasLineManager,
        },
      ],
      maxItems: 10,
      minItems: 1,
    },
  ];

  return (
    <Box p={2}>
      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <DeleteDialog
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onSuccess={onSuccess}
        url={`/api/approval-process/delete/${deleteId}`}
        title="Delete Approval Process"
        description="Are you sure you want to delete this approval process?"
        confirmText="Delete"
      />

      <FormDialog
        open={open > 0}
        setOpen={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}
        title={open === 2 ? "Edit Approval Process" : "Add Approval Process"}
        fields={approvalProcessFields}
        url={
          open === 2 && selectedId
            ? `/api/approval-process/update/${selectedId}`
            : "/api/approval-process/create/"
        }
        onSuccess={onSuccess}
        maxWidth="sm"
        col={12}
        buttonTitle={
          open === 2 ? "Update Approval Process" : "Save Approval Process"
        }
        beforeSubmit={beforeSubmit}
      />

      <DatatableComponent
        columns={[
          {
            name: "positionId",
            label: "Position",
            options: {
              customBodyRender: (value) =>
                positions.find((p) => p.id === value)?.name || value,
            },
          },
          ...stepColumns,
          {
            name: "id",
            label: "Actions",
            options: {
              filter: false,
              sort: false,
              customBodyRender: (id, tableMeta) => {
                const row = tableData[tableMeta.rowIndex];
                return (
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(row)}
                      aria-label="Edit"
                    >
                      <img
                        src={require("../../assets/icons/pencil.svg").default}
                        alt="Edit"
                        className="mr-2 text-sm"
                        style={{ width: 16, height: 16 }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(id)}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              },
            },
          },
        ]}
        url={`/api/approval-process/list/?count=${count}`}
        title="Approval Processes"
        processData={processData}
        elevation={1}
        searchfield={false}
        search={false}
        viewColumns={false}
        addButton={true}
        onAddNew={() => {
          resetForm();
          setOpen(1);
        }}
      />
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApprovalProcessComponent);
